import { GameEngine } from './lib/GameEngine.js';
import { DEFAULT_THEME } from './lib/Hud.js';
import { parseGameConfig } from './lib/schema.js';
import { StartMenu } from './lib/StartMenu.js';
import { OptionsWindow } from './lib/OptionsWindow.js';
import * as SaveState from './lib/SaveState.js';
import { confirmDialog } from './lib/confirmDialog.js';

declare const GAME_ID: string | undefined;

type HashValue = string | number;

function getLocHash(): Record<string, HashValue> {
    if (location.hash.length < 2) return {};

    const hash: Record<string, HashValue> = {};
    const hashParts = location.hash.substring(1).split('&');

    hashParts.forEach((kv) => {
        const intMatch = kv.match(/^(.+?)=(\d+)$/);
        const floatMatch = kv.match(/^(.+?)=(\d+\.\d+)$/);
        const stringMatch = kv.match(/^(.+?)=(.*)$/);

        if (intMatch) hash[intMatch[1]!] = parseInt(intMatch[2]!, 10);
        else if (floatMatch) hash[floatMatch[1]!] = parseFloat(floatMatch[2]!);
        else if (stringMatch) {
            try {
                hash[stringMatch[1]!] = decodeURIComponent(stringMatch[2]!);
            } catch {
                // Ignore malformed hash values and let the missing id error show.
            }
        }
    });

    return hash;
}

function showError(message: string, detail?: string): void {
    document.getElementById('errorMessage')!.textContent = message;
    document.getElementById('errorDetail')!.textContent = detail ?? '';
    document.getElementById('errorScreen')!.classList.remove('hidden');
    document.getElementById('loadingScreen')!.classList.add('hidden');
}

const locHash = getLocHash();
const gameId = typeof GAME_ID !== 'undefined' ? GAME_ID : (locHash.id as string | undefined);

async function getGameData(onOptions: () => void): Promise<void> {
    if (!gameId) {
        showError('No game selected.', 'Open this page as index.html#id=<game-uuid>');
        return;
    }

    if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(gameId)) {
        showError('Invalid game selected.', 'The game id must be a UUID.');
        return;
    }

    const url = `data/${gameId}/data.json`;
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Response status: ${response.status}`);

        const config = parseGameConfig(await response.json());
        const engine = new GameEngine(config, gameId, onOptions);
        await engine.ready;
    } catch (error) {
        console.error(error);
        const message = error instanceof Error ? error.message : String(error);
        showError('Could not load this game.', `${url} — ${message}`);
    }
}

/**
 * Mirrors OptionsWindow's restart flow so the start-menu and in-game reset
 * buttons behave identically.
 */
async function resetGame(): Promise<void> {
    if (!gameId) return;

    const confirmed = await confirmDialog({
        title: 'Restart game',
        message: 'Restart the game from Level 1? Your current progress will be lost.',
        confirmLabel: 'Restart',
    });
    if (!confirmed) return;

    SaveState.suppressSaves();
    SaveState.clear(gameId);
    window.location.reload();
}

/**
 * The theme reaches #gameContainer only when START finishes loading the
 * config, so the start screen would otherwise show wood chrome. This
 * best-effort fetch reads ui.interface early; failures surface through the
 * real load on START.
 */
if (gameId) {
    void fetch(`data/${gameId}/data.json`)
        .then((response) => (response.ok ? response.json() : null))
        .then((raw) => {
            if (!raw) return;
            const container = document.getElementById('gameContainer');
            if (container) container.dataset.theme = parseGameConfig(raw).ui?.interface || DEFAULT_THEME;
        })
        .catch(() => {
            // The real load on START reports errors.
        });
}

const optionsWindow = new OptionsWindow({ gameId });
new StartMenu({
    onStart: () => void getGameData(() => optionsWindow.toggle()),
    onOptions: () => optionsWindow.toggle(),
    onReset: () => void resetGame(),
});
